import { LitElement, html } from "lit";

import { customElement, property, query, state } from "lit/decorators.js";

import { ResizeObserverController } from "ui-common";
import "ui-components/form-elements/input/wow-input";

import { styles } from "./advanced-color-picker.css";
import {
  type HSV,
  canvasToHsv,
  hexToHsv,
  hsvToCanvas,
  hsvToHex,
  hsvToRgb,
  hueToSlider,
  isValidHex,
  sliderToHue,
} from "./color-utils";

import { t } from "../../translations/i18n";

@customElement("advanced-color-picker")
export class AdvancedColorPicker extends LitElement {
  static override styles = styles;

  @property({ type: String })
  value = "#FF5733";

  @property({ type: String })
  label = "";

  @state()
  private currentHsv: HSV = { h: 0, s: 1, v: 1 };

  @state()
  private hexInput = "";

  @state()
  private hasError = false;

  @state()
  private errorMessage = "";

  @state()
  private isDraggingCanvas = false;

  @state()
  private isDraggingSlider = false;

  @query(".color-canvas")
  private canvas!: HTMLCanvasElement;

  @query(".color-canvas-container")
  private canvasContainer!: HTMLElement;

  @query(".hue-slider-container")
  private sliderContainer!: HTMLElement;

  private canvasResizeController = new ResizeObserverController(this, () => this.canvasContainer);
  private sliderResizeController = new ResizeObserverController(this, () => this.sliderContainer);

  /**
   * Computed property that determines if the slider is ready based on resize controller data
   */
  private get isSliderReady(): boolean {
    const sliderSize = this.sliderResizeController.result;
    return Boolean(sliderSize && sliderSize.width > 0);
  }

  override connectedCallback() {
    super.connectedCallback();
    this.initializeFromValue();

    // Add global event listeners for drag operations
    document.addEventListener("mousemove", this.handleGlobalMouseMove);
    document.addEventListener("mouseup", this.handleGlobalMouseUp);
    document.addEventListener("touchmove", this.handleGlobalTouchMove, { passive: false });
    document.addEventListener("touchend", this.handleGlobalTouchEnd);
  }

  override disconnectedCallback() {
    super.disconnectedCallback();

    // Remove global event listeners
    document.removeEventListener("mousemove", this.handleGlobalMouseMove);
    document.removeEventListener("mouseup", this.handleGlobalMouseUp);
    document.removeEventListener("touchmove", this.handleGlobalTouchMove);
    document.removeEventListener("touchend", this.handleGlobalTouchEnd);
  }

  override willUpdate(changedProperties: Map<string | number | symbol, unknown>) {
    if (changedProperties.has("value") && !this.isDraggingCanvas && !this.isDraggingSlider) {
      this.initializeFromValue();
    }
  }

  override firstUpdated() {
    this.updateCanvas();
    this.adjustCanvasSize();
  }

  override updated(changedProperties: Map<string | number | symbol, unknown>) {
    if (changedProperties.has("currentHsv")) {
      this.updateCanvas();
    }

    // Check if canvas size changed and adjust accordingly
    const canvasSize = this.canvasResizeController.result;
    if (canvasSize) {
      this.adjustCanvasSize();
    }
  }

  private initializeFromValue() {
    const hsv = hexToHsv(this.value);
    if (hsv) {
      this.currentHsv = hsv;
      this.hexInput = this.value.toUpperCase();
      this.clearError();
    } else {
      // Fallback to default
      this.currentHsv = { h: 0, s: 1, v: 1 };
      this.hexInput = "#FF5733";
    }
  }

  private adjustCanvasSize() {
    if (!this.canvas || !this.canvasContainer) return;

    // Set canvas size for responsive design
    const rect = this.canvasContainer.getBoundingClientRect();

    this.canvas.width = rect.width;
    this.canvas.height = rect.height;

    this.updateCanvas();
  }

  private updateCanvas() {
    if (!this.canvas) return;

    const ctx = this.canvas.getContext("2d");
    if (!ctx) return;

    const { width, height } = this.canvas;

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Create saturation gradient (left to right)
    const saturationGradient = ctx.createLinearGradient(0, 0, width, 0);
    const baseColor = hsvToRgb(this.currentHsv.h, 1, 1);
    saturationGradient.addColorStop(0, `rgb(255, 255, 255)`);
    saturationGradient.addColorStop(1, `rgb(${baseColor.r}, ${baseColor.g}, ${baseColor.b})`);

    ctx.fillStyle = saturationGradient;
    ctx.fillRect(0, 0, width, height);

    // Create brightness gradient (top to bottom)
    const brightnessGradient = ctx.createLinearGradient(0, 0, 0, height);
    brightnessGradient.addColorStop(0, "rgba(0, 0, 0, 0)");
    brightnessGradient.addColorStop(1, "rgba(0, 0, 0, 1)");

    ctx.fillStyle = brightnessGradient;
    ctx.fillRect(0, 0, width, height);
  }

  private emitColorChange() {
    const hex = hsvToHex(this.currentHsv.h, this.currentHsv.s, this.currentHsv.v);
    this.dispatchEvent(
      new CustomEvent("color-change", {
        detail: { hex, hsv: this.currentHsv },
        bubbles: true,
      }),
    );
  }

  private clearError() {
    this.hasError = false;
    this.errorMessage = "";
  }

  private setError(message: string) {
    this.hasError = true;
    this.errorMessage = message;
  }

  // Canvas interaction handlers
  private handleCanvasMouseDown = (event: MouseEvent) => {
    this.isDraggingCanvas = true;
    this.handleCanvasInteraction(event.offsetX, event.offsetY);
    event.preventDefault();
  };

  private handleCanvasTouchStart = (event: TouchEvent) => {
    this.isDraggingCanvas = true;
    const rect = this.canvas.getBoundingClientRect();
    const touch = event.touches[0]!;
    const x = touch.clientX - rect.left;
    const y = touch.clientY - rect.top;
    this.handleCanvasInteraction(x, y);
    event.preventDefault();
  };

  private handleCanvasInteraction(x: number, y: number) {
    if (!this.canvas) return;

    const rect = this.canvas.getBoundingClientRect();
    const { s, v } = canvasToHsv(x, y, rect.width, rect.height);

    // Preserve the current hue value when updating saturation and value
    const preservedHue = this.currentHsv.h;
    this.currentHsv = { h: preservedHue, s, v };
    this.hexInput = hsvToHex(this.currentHsv.h, this.currentHsv.s, this.currentHsv.v);
    this.clearError();
    this.emitColorChange();
  }

  // Slider interaction handlers
  private handleSliderMouseDown = (event: MouseEvent) => {
    this.isDraggingSlider = true;
    this.handleSliderInteraction(event.offsetX);
    event.preventDefault();
  };

  private handleSliderTouchStart = (event: TouchEvent) => {
    this.isDraggingSlider = true;
    const rect = this.sliderContainer.getBoundingClientRect();
    const touch = event.touches[0]!;
    const x = touch.clientX - rect.left;
    this.handleSliderInteraction(x);
    event.preventDefault();
  };

  private handleSliderInteraction(x: number) {
    const sliderWidth = this.sliderContainer.getBoundingClientRect().width;
    const h = sliderToHue(x, sliderWidth);
    this.currentHsv = { ...this.currentHsv, h };
    this.hexInput = hsvToHex(this.currentHsv.h, this.currentHsv.s, this.currentHsv.v);
    this.clearError();
    this.emitColorChange();
  }

  // Global mouse/touch handlers for dragging
  private handleGlobalMouseMove = (event: MouseEvent) => {
    if (this.isDraggingCanvas) {
      const rect = this.canvas.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      this.handleCanvasInteraction(x, y);
    } else if (this.isDraggingSlider) {
      const rect = this.sliderContainer.getBoundingClientRect();
      const x = event.clientX - rect.left;
      this.handleSliderInteraction(x);
    }
  };

  private handleGlobalMouseUp = () => {
    this.isDraggingCanvas = false;
    this.isDraggingSlider = false;
  };

  private handleGlobalTouchMove = (event: TouchEvent) => {
    if (this.isDraggingCanvas || this.isDraggingSlider) {
      event.preventDefault();
      const touch = event.touches[0]!;

      if (this.isDraggingCanvas) {
        const rect = this.canvas.getBoundingClientRect();
        const x = touch.clientX - rect.left;
        const y = touch.clientY - rect.top;
        this.handleCanvasInteraction(x, y);
      } else if (this.isDraggingSlider) {
        const rect = this.sliderContainer.getBoundingClientRect();
        const x = touch.clientX - rect.left;
        this.handleSliderInteraction(x);
      }
    }
  };

  private handleGlobalTouchEnd = () => {
    this.isDraggingCanvas = false;
    this.isDraggingSlider = false;
  };

  // Hex input handlers
  private handleHexInputChange = (event: Event) => {
    const target = event.target as HTMLInputElement;
    let value = target.value;

    // Auto-add # if missing
    if (value && !value.startsWith("#")) {
      value = "#" + value;
      target.value = value;
    }

    this.hexInput = value;

    if (isValidHex(value)) {
      const hsv = hexToHsv(value);
      if (hsv) {
        // Preserve hue for grayscale colors (saturation = 0)
        const finalHsv = hsv.s === 0 ? { ...hsv, h: this.currentHsv.h } : hsv;
        this.currentHsv = finalHsv;
        this.clearError();
        this.emitColorChange();
      }
    } else if (value.length >= 7) {
      this.setError("Please enter a valid hex color (e.g., #FF5733)");
    } else {
      this.clearError();
    }
  };

  private getCanvasCursorPosition() {
    if (!this.canvas) return { x: 0, y: 0 };

    const rect = this.canvasResizeController.result!;
    const { x, y } = hsvToCanvas(this.currentHsv.s, this.currentHsv.v, rect.width, rect.height);
    return { x, y };
  }

  private getSliderCursorPosition() {
    const sliderSize = this.sliderResizeController.result;
    if (!sliderSize || sliderSize.width <= 0) {
      return 0;
    }
    return hueToSlider(this.currentHsv.h, sliderSize.width);
  }

  protected override render() {
    const cursorPos = this.getCanvasCursorPosition();
    const sliderPos = this.getSliderCursorPosition();

    return html`
      <div class="color-picker-container">
        <!-- Visual picker container (canvas + hue slider with no gap) -->
        <div class="visual-picker-container">
          <!-- 2D Color Canvas -->
          <div class="color-canvas-container">
            <canvas
              class="color-canvas"
              @mousedown=${this.handleCanvasMouseDown}
              @touchstart=${this.handleCanvasTouchStart}
            ></canvas>
            <div class="canvas-cursor" style="left: ${cursorPos.x}px; top: ${cursorPos.y}px;"></div>
          </div>

          <!-- 1D Hue Slider -->
          <div class="hue-slider-container">
            <div
              class="hue-slider"
              @mousedown=${this.handleSliderMouseDown}
              @touchstart=${this.handleSliderTouchStart}
            ></div>
            ${this.isSliderReady ? html`<div class="hue-cursor" style="left: ${sliderPos}px;"></div>` : ""}
          </div>
        </div>

        <!-- Input container with label and input -->
        <div class="input-container">
          ${this.label ? html`<h3 class="color-picker-title">${this.label}</h3>` : ""}
          <wow-input
            type="text"
            .value=${this.hexInput}
            placeholder=${t("profile.hexColor")}
            @input=${this.handleHexInputChange}
          ></wow-input>
          ${this.hasError ? html`<div class="error-message">${this.errorMessage}</div>` : ""}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "advanced-color-picker": AdvancedColorPicker;
  }
}
